import { sanitizeExistingResourceContext } from "src/app/models/provisioning/provisioning-context-sanitization";
import { Action, ActionReducer, createReducer, on } from "@ngrx/store";
import { deleteProvisioningContext, setProvisioningContext, updateCurrentStep, updateProvisioningContextContentAndStructure, updateProvisioningContextGovernance, updateProvisioningContextLocations, updateProvisioningContextLocationsToApply, updateProvisioningContextPolicies, updateProvisioningContextProducts, updateProvisioningContextProductsAmount, updateProvisioningContextProductsToInstall, updateProvisioningContextResourcesDetails, updateProvisioningContextUserResourcesForUpdate, updateProvisioningContextVariableContent, updateProvisioningDocumentTemplates, updateProvisioningHandleMultipleInstallations, updateProvisioningHubSiteAssociation, updateProvisioningInstallationMode, updateProvisioningAdministrators, updateProvisioningMembers, updateProvisioningOwners, updateProvisioningVisitors, updateProvisioningPages, updateProvisioningProductsInstallationScope, updateProvisioningReuseLogos, updateProvisioningScope, updateProvisioningSiteLogo, updateProvisioningSiteStorageLimit, updateProvisioningSiteThumbnailLogo, updateProvisioningTeamLogo, updateProvisioningVariableContentValue, updateSelectiveProvisioningWebSettings, updateSensitivityLabel } from "../actions/provisioning.actions";
import { ProvisioningContextState, provisioningContextInitialState } from "../initial-states/provisioning.state";
import { UserResourceInterfaceType, UserResourceType } from "src/app/models/user-resources.model";
import { EnvironmentScope, ProvisioningProduct, TemplateVariableContent } from "src/app/models/provisioning-context.model";
import { ProductTypeEnum } from "src/app/models/enums.model";


export const ProvisioningContextReducer: ActionReducer<ProvisioningContextState, Action> = createReducer(provisioningContextInitialState,

	// The reducer runs when a dispatch 'setProvisioningContext' was triggered
	// The only thing we need to do here is to set context to the receiving object
	on(setProvisioningContext,
		(state, { value }) => ({
			...state, value: sanitizeExistingResourceContext(value, { preserveTemplateSettings: true })
		}),
	),

	// The reducer runs when a dispatch 'deleteProvisioningContext' was triggered
	// The only thing we need to do here is to set context to the undefined
	on(deleteProvisioningContext,
		(state) => ({
			...state, value: undefined
		}),
	),

	// The reducer runs when a dispatch 'updateCurrentStep' was triggered
	// The only thing we need to do here update the current step
	on(updateCurrentStep,
		(state, { currentStep }) => ({
			...state, value: state.value ? { ...state.value, currentStep } : undefined
		}),
	),

	// The reducer runs when a dispatch 'updateProvisioningContextProducts' was triggered
	// The only thing we need to do here update the products
	on(updateProvisioningContextProducts,
		(state, { products }) => ({
			...state, value: state.value ? { ...state.value, products } : undefined
		}),
	),

	// The reducer runs when a dispatch 'updateProvisioningContextProductsToInstall' was triggered
	// The only thing we need to do here update the productsToInstall
	on(updateProvisioningContextProductsToInstall,
		(state, { productsToInstall, doNotUpdateInstalledWebParts }) => ({
			...state, value: state.value ? { ...state.value, versionSelection: { doNotUpdateOutdatedProduct: doNotUpdateInstalledWebParts, productVersionSelection: productsToInstall } } : undefined
		}),
	),

	// The reducer runs when a dispatch 'updateProvisioningContextLocations' was triggered
	// The only thing we need to do here update the locations
	on(updateProvisioningContextLocations,
		(state, { locations }) => ({
			...state, value: state.value ? { ...state.value, locations } : undefined
		}),
	),

	// The reducer runs when a dispatch 'updateProvisioningContextLocationsToApply' was triggered
	// The only thing we need to do here update the locations
	on(updateProvisioningContextLocationsToApply,
		(state, { locationsToApply }) => ({
			...state, value: state.value ? { ...state.value, locationsToApply: locationsToApply } : undefined
		}),
	),

	// The reducer runs when a dispatch 'updateProvisioningContextResourceDetails' was triggered
	// The only thing we need to do here update the locations
	on(updateProvisioningContextResourcesDetails,
		(state, { resourcesDetails }) => ({
			...state, value: state.value ? { ...state.value, resourcesDetails } : undefined
		}),
	),

	// The reducer runs when a dispatch 'updateProvisioningScope' was triggered
	// The only thing we need to do here update the provisioning scope
	on(updateProvisioningScope,
		(state, { provisioningScope }) => ({
			...state,
			value: state.value
				? sanitizeExistingResourceContext({ ...state.value, provisioningScope }, { preserveTemplateSettings: true })
				: undefined
		}),
	),

	// The reducer runs when a dispatch 'updateProvisioningInstallationMode' was triggered
	// The only thing we need to do here update the provisioning scope
	on(updateProvisioningInstallationMode,
		(state, { installationMode }) => ({
			...state,
			value: state.value ? { ...state.value, installationMode } : undefined
		}),
	),

	// The reducer runs when a dispatch 'updateProvisioningHandleMultipleInstallations' was triggered
	// The only thing we need to do here update the handleMultipleInstallations flag
	on(updateProvisioningHandleMultipleInstallations,
		(state, { handleMultipleInstallations }) => ({
			...state,
			value: state.value ? { ...state.value, handleMultipleInstallations } : undefined
		}),
	),

	// The reducer runs when a dispatch 'updateProvisioningContextVariableContent' was triggered
	// The only thing we need to do here update the variable content object
	on(updateProvisioningContextVariableContent,
		(state, { variableContent }) => ({
			...state, value: state.value ? { ...state.value, variableContent } : undefined
		}),
	),

	// The reducer runs when a dispatch 'updateProvisioningContextPolicies' was triggered
	// The only thing we need to do here update the policies object
	on(updateProvisioningContextPolicies,
		(state, { policies }) => ({
			...state, value: state.value ? { ...state.value, policies } : undefined
		}),
	),

	// The reducer runs when a dispatch 'updateProvisioningContextGovernance' was triggered
	// The only thing we need to do here update the policies object
	on(updateProvisioningContextGovernance,
		(state, { governance }) => ({
			...state, value: state.value ? { ...state.value, governance } : undefined
		}),
	),

	// The reducer runs when a dispatch 'updateProvisioningContextUserResourcesForUpdate' was triggered
	// The only thing we need to do here update the user resources for update based on the resource type
	on(updateProvisioningContextUserResourcesForUpdate,
		(state, { resourceType, userResourcesForUpdate }) => ({
			...state, value: state.value ? { ...state.value, userResourcesForUpdate: { ...state.value.userResourcesForUpdate, [resourceType]: UpdateUserResourcesForUpdate(resourceType, resourceType === 'hubsites' || resourceType === 'sites' ? state.value.userResourcesForUpdate[resourceType] : [], userResourcesForUpdate) } } : undefined
		}),
	),

	// The reducer runs when a dispatch 'updateProvisioningContextProductsAmount' was triggered
	// The only thing we need to do here update the policies object
	on(updateProvisioningContextProductsAmount,
		(state, { productsAmount }) => ({
			...state, value: state.value ? { ...state.value, productsAmount } : undefined
		}),
	),

	on(updateProvisioningContextContentAndStructure,
		(state, { contentAndStructure }) => ({
			...state, value: state.value ? { ...state.value, contentAndStructure } : undefined
		}),
	),

	on(updateProvisioningPages,
		(state, { pages }) => ({
			...state, value: state.value ? { ...state.value, pages } : undefined
		}),
	),

	on(updateProvisioningDocumentTemplates,
		(state, { documentTemplates }) => ({
			...state, value: state.value ? { ...state.value, documentTemplates: documentTemplates } : undefined
		}),
	),

	on(updateProvisioningHubSiteAssociation,
		(state, { hubSiteAssociation }) => ({
			...state, value: state.value ? { ...state.value, governance: { ...state.value.governance, hubSiteAssociation: hubSiteAssociation } } : undefined
		}),
	),

	on(updateProvisioningSiteStorageLimit,
		(state, { siteStorageLimit }) => ({
			...state, value: state.value ? { ...state.value, governance: { ...state.value.governance, sharePointSiteLimit: siteStorageLimit } } : undefined
		}),
	),

	// -------- SP CATALOG --------- //

	// The reducer runs when a dispatch 'updateProvisioningContextProductsAmount' was triggered
	// The only thing we need to do here update the installation scope
	on(updateProvisioningProductsInstallationScope,
		(state, { scope }) => ({
			...state, value: state.value ? { ...state.value, products: updateInstallationScope(state.value.products, scope) } : undefined
		}),
	),

	// The reducer runs when a dispatch 'updateProvisioningContextProductsAmount' was triggered
	// The only thing we need to do here update the token values
	on(updateProvisioningVariableContentValue,
		(state, { tokens }) => ({
			...state, value: state.value ? { ...state.value, variableContent: updateTokensValues(state.value.variableContent, tokens) } : undefined
		}),
	),

	// The reducer runs when a dispatch 'updateProvisioningSelectiveProvisioning' was triggered
	// The only thing we need to do here update the selectiveProvisioning object
	on(updateSelectiveProvisioningWebSettings,
		(state, { webSettings }) => ({
			...state, value: state.value ? { ...state.value, selectiveProvisioning: { ...state.value.selectiveProvisioning, office365: { ...state.value.selectiveProvisioning?.office365, provisioningTemplate: { ...state.value.selectiveProvisioning?.office365?.provisioningTemplate, webSettings } } } } : undefined
		}),
	),

	// The reducer runs when a dispatch 'updateSensitivityLabel' was triggered
	// The only thing we need to do here update the sensitivity label object
	on(updateSensitivityLabel,
		(state, { sensitivityLabel }) => ({
			...state, value: state.value ? { ...state.value, governance: { ...state.value.governance, sensitivityLabel } } : undefined
		}),
	),

	// The reducer runs when a dispatch 'updateSensitivityLabel' was triggered
	// The only thing we need to do here update the owners array
	on(updateProvisioningOwners,
		(state, { owners }) => ({
			...state, value: state.value ? { ...state.value, governance: { ...state.value.governance, owners } } : undefined
		}),
	),

	// The reducer runs when a dispatch 'updateProvisioningMembers' was triggered
	// The only thing we need to do here update the members array
	on(updateProvisioningMembers,
		(state, { members }) => ({
			...state, value: state.value ? { ...state.value, governance: { ...state.value.governance, members } } : undefined
		}),
	),

	// The reducer runs when a dispatch 'updateProvisioningAdministrators' was triggered
	// The only thing we need to do here update the administrators array
	on(updateProvisioningAdministrators,
		(state, { administrators }) => ({
			...state, value: state.value ? { ...state.value, governance: { ...state.value.governance, administrators } } : undefined
		}),
	),

	// The reducer runs when a dispatch 'updateProvisioningVisitors' was triggered
	// The only thing we need to do here update the visitors array
	on(updateProvisioningVisitors,
		(state, { visitors }) => ({
			...state, value: state.value ? { ...state.value, governance: { ...state.value.governance, visitors } } : undefined
		}),
	),

	// The reducer runs when a dispatch 'updateProvisioningTeamLogo' was triggered
	// The only thing we need to do here update the team logo object
	on(updateProvisioningTeamLogo,
		(state, { logo }) => ({
			...state, value: state.value ? { ...state.value, governance: { ...state.value.governance, teamLogo: logo } } : undefined
		}),
	),

	// The reducer runs when a dispatch 'updateProvisioningSiteLogo' was triggered
	// The only thing we need to do here update the site logo object
	on(updateProvisioningSiteLogo,
		(state, { logo }) => ({
			...state, value: state.value ? { ...state.value, governance: { ...state.value.governance, siteLogo: logo } } : undefined
		}),
	),

	// The reducer runs when a dispatch 'updateProvisioningSiteThumbnailLogo' was triggered
	// The only thing we need to do here update the site thumbnail logo object
	on(updateProvisioningSiteThumbnailLogo,
		(state, { logo }) => ({
			...state, value: state.value ? { ...state.value, governance: { ...state.value.governance, siteLogoThumbnail: logo } } : undefined
		}),
	),

	// The reducer runs when a dispatch 'updateProvisioningReuseLogos' was triggered
	// The only thing we need to do here update the reuse logos object
	on(updateProvisioningReuseLogos,
		(state, { isToReuseLogo }) => ({
			...state, value: state.value ? { ...state.value, governance: { ...state.value.governance, isToReuseLogo: isToReuseLogo } } : undefined
		}),
	),
)

function UpdateUserResourcesForUpdate(resourceType: UserResourceType, currentListOfResources: UserResourceInterfaceType, resourcesToUpdate: UserResourceInterfaceType) {
	switch (resourceType) {
		case 'sites':
		case 'hubsites':
			resourcesToUpdate.forEach(r => {
				const index = currentListOfResources.findIndex((site) => site.url === r.url);
				if (index !== -1) {
					currentListOfResources[index] = r;
				}
			})
			break;
		default:
			break;
	}

	return currentListOfResources
}


// SP CATALOG
function updateInstallationScope(products: ProvisioningProduct[], scope: EnvironmentScope) {
	products.forEach(product => {
		if (!(product.type === ProductTypeEnum.Template)) {
			product.installScope = scope;
		}
	});
	return products
}

function updateTokensValues(variableContent: TemplateVariableContent[], tokens: { token: string, newValue: string }[]) {
	for (const token of tokens) {
		for (let i = 0; i < variableContent.length; i++) {
			const index = variableContent[i].tokens.findIndex(x => x.token === token.token)
			if (index !== -1) {
				variableContent[i].tokens[index].value = token.newValue
				break;
			}
		}
	}
	return variableContent
}
